/**
 * SessionCountdown — live mm:ss display + colour-coded urgency.
 *
 * Used in two places (DRY):
 *   - AgentConsole issued-code panel: the agent sees how long the
 *     code is valid for, so they can reissue before reading it out.
 *   - Chatroom header: both parties see a shared expiry clock so the
 *     conversation pace matches the budget.
 *
 * Visual states:
 *   normal   ≥ 5 min remaining → slate text
 *   warning  < 5 min          → amber text
 *   urgent   < 1 min          → rose text + bold
 *   expired  = 0              → "Expired" instead of mm:ss
 *
 * The component is presentational — it owns no business logic. The
 * caller passes `expiresAt` (UTC ms epoch) and an optional `label`.
 */
import { useCountdown } from '../hooks/useCountdown'

export interface SessionCountdownProps {
  /** UTC ms epoch the session expires at. `null` renders "Expired". */
  readonly expiresAt: number | null
  /** Optional prefix label, e.g. "Session expires in" or "Time left". */
  readonly label?: string
  /** Visual size variant. */
  readonly size?: 'sm' | 'md' | 'lg'
}

const URGENT_THRESHOLD_MS = 60 * 1000      // 1 minute
const WARNING_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

export function SessionCountdown({
  expiresAt,
  label = 'Time left',
  size = 'md',
}: SessionCountdownProps) {
  const { remainingMs, expired, formatted } = useCountdown(expiresAt)

  const tone = expired
    ? 'text-rose-700'
    : remainingMs < URGENT_THRESHOLD_MS
      ? 'text-rose-700 font-semibold'
      : remainingMs < WARNING_THRESHOLD_MS
        ? 'text-amber-700'
        : 'text-slate-600'

  const sizeClass = SIZE_CLASSES[size]

  return (
    <span
      className={`inline-flex items-baseline gap-2 tabular-nums ${sizeClass} ${tone}`}
      // aria-live=polite so screen readers announce changes without
      // interrupting; aria-atomic so the whole label+time is read
      // together rather than just the changing digits.
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="text-slate-500">{label}</span>
      <span className="font-mono">
        {expired ? 'Expired' : formatted}
      </span>
    </span>
  )
}

const SIZE_CLASSES: Record<NonNullable<SessionCountdownProps['size']>, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
}
